import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MapLayerMouseEvent, GeoJSONSource, HeatmapPaint } from 'mapbox-gl';
import ReactMapGL, {
  Source,
  Layer,
  Popup,
  NavigationControl,
  GeolocateControl,
  FullscreenControl,
  ScaleControl,
  type MapRef,
  type ViewStateChangeEvent,
} from 'react-map-gl';
import { Box, Button, IconButton, Tooltip, Typography, Stack } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import MapIcon from '@mui/icons-material/Map';
import CropFreeIcon from '@mui/icons-material/CropFree';
import type { WebMapPersistedWidgetConfig, WebMapWidgetOptions, MapControlToggles, MapBookmark, MapAnnotation } from '../types/webMap';
import { fetchLayerGeoJson, fetchLayerColumns, inferColumnsFromFeatures } from '@signals/app-shell/services/mapLayersService';
import { fetchSourceGeoJson } from './sourceCatalogService';
import { useWebMapContext, type MapExtent } from './WebMapContext';
import WebMapEditorDialog from './WebMapEditorDialog';
import {
  categoryColorExpression,
  categoryIconExpression,
  webMapGeoJsonSourceKey,
  heatmapWeightPaint,
} from './webMapMapboxUtils';
import { popupPropertyEntries } from './webMapPopupUtils';
import MapToolbar, { type MapTool } from './MapToolbar';
import MapMeasureOverlay, { type MeasurePoint } from './MapMeasureOverlay';
import MapLegendOverlay from './MapLegendOverlay';
import MapBookmarksPanel from './MapBookmarksPanel';
import MapDrawControl from './MapDrawControl';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN ?? '';

const BASEMAP_STYLES: Record<string, string> = {
  streets: 'mapbox://styles/mapbox/streets-v12',
  satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
  dark: 'mapbox://styles/mapbox/dark-v11',
  light: 'mapbox://styles/mapbox/light-v11',
  outdoors: 'mapbox://styles/mapbox/outdoors-v12',
};

const LAYER_PALETTE = ['#1976d2', '#9c27b0', '#00bcd4', '#ff5722', '#795548', '#3f51b5', '#cddc39', '#e91e63'];

// @ts-ignore - Mapbox Draw types
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { useControl } from 'react-map-gl';

const GEOFENCE_DRAW_STYLES = [
  { id: 'gf-fill-inactive', type: 'fill', filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']], paint: { 'fill-color': '#f44336', 'fill-opacity': 0.15 } },
  { id: 'gf-fill-active', type: 'fill', filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']], paint: { 'fill-color': '#f44336', 'fill-opacity': 0.25 } },
  { id: 'gf-stroke-inactive', type: 'line', filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']], layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#f44336', 'line-width': 2 } },
  { id: 'gf-stroke-active', type: 'line', filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']], layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#f44336', 'line-width': 2.5 } },
  { id: 'gf-vertex-halo', type: 'circle', filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']], paint: { 'circle-radius': 7, 'circle-color': '#FFF' } },
  { id: 'gf-vertex', type: 'circle', filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']], paint: { 'circle-radius': 5, 'circle-color': '#f44336' } },
  { id: 'gf-midpoint', type: 'circle', filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']], paint: { 'circle-radius': 4, 'circle-color': '#f44336' } },
];

const GeofenceDrawOverlay: React.FC<{ onComplete: (g: { type: 'Polygon'; coordinates: number[][][] }) => void }> = ({ onComplete }) => {
  // @ts-ignore
  useControl<any>(
    () => new MapboxDraw({ displayControlsDefault: false, controls: { polygon: true, trash: true }, defaultMode: 'draw_polygon', styles: GEOFENCE_DRAW_STYLES }),
    ({ map }) => { map.on('draw.create', (e: any) => { if (e.features[0]?.geometry?.type === 'Polygon') onComplete(e.features[0].geometry); }); },
    ({ map }) => { map.off('draw.create', () => {}); },
    { position: 'top-right' },
  );
  return null;
};

interface WebMapWidgetProps {
  widgetConfig?: WebMapPersistedWidgetConfig;
  layoutId: string;
  editMode?: boolean;
  onUpdateConfig?: (config: WebMapPersistedWidgetConfig) => void;
  /** Controlled editor open state from parent (e.g. header button). */
  editorOpen?: boolean;
  onEditorOpenChange?: (open: boolean) => void;
}

const WebMapWidget: React.FC<WebMapWidgetProps> = ({
  widgetConfig,
  layoutId: _layoutId,
  editMode,
  onUpdateConfig,
  editorOpen: controlledEditorOpen,
  onEditorOpenChange,
}) => {
  const mapRef = useRef<MapRef>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const webMapCtx = useWebMapContext();
  const [internalEditorOpen, setInternalEditorOpen] = useState(false);
  const editorOpen = controlledEditorOpen ?? internalEditorOpen;
  const setEditorOpen = useCallback((open: boolean) => {
    onEditorOpenChange ? onEditorOpenChange(open) : setInternalEditorOpen(open);
  }, [onEditorOpenChange]);
  const [popupInfo, setPopupInfo] = useState<{
    lng: number;
    lat: number;
    properties: Record<string, unknown>;
    layerId: string;
  } | null>(null);

  // --- Map tools state ---
  const [activeTool, setActiveTool] = useState<MapTool>(null);
  const [showLegend, setShowLegend] = useState(false);
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [measurePoints, setMeasurePoints] = useState<MeasurePoint[]>([]);
  const [bookmarks, setBookmarks] = useState<MapBookmark[]>([]);

  const opts: WebMapWidgetOptions = widgetConfig?.webMapOptions ?? { layers: [] };
  const layers = opts.layers ?? [];
  const basemapKey = opts.basemap ?? 'streets';
  const mapStyle = BASEMAP_STYLES[basemapKey] ?? BASEMAP_STYLES.streets;
  const ctrl: MapControlToggles = opts.controls ?? {};

  useEffect(() => {
    setBookmarks(opts.bookmarks ?? []);
  }, [opts.bookmarks?.length]);
  const showNav = ctrl.navigation !== false;
  const showGeo = ctrl.geolocate !== false;
  const showFs = ctrl.fullscreen !== false;
  const showScale = ctrl.scale !== false;

  const initialExtent = opts.initialExtent;
  const [viewport, setViewport] = useState({
    longitude: initialExtent?.center?.[0] ?? -98.5,
    latitude: initialExtent?.center?.[1] ?? 39.8,
    zoom: initialExtent?.zoom ?? 4,
  });

  useEffect(() => {
    const c = initialExtent?.center;
    if (!c || initialExtent?.zoom == null) return;
    setViewport((v) => ({
      ...v,
      longitude: c[0],
      latitude: c[1],
      zoom: initialExtent.zoom,
    }));
  }, [initialExtent?.center?.[0], initialExtent?.center?.[1], initialExtent?.zoom]);

  // --- 3D Terrain toggle ---
  const showTerrain = ctrl.terrain3d !== false;
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const apply = () => {
      if (showTerrain) {
        if (!map.getSource('mapbox-dem')) {
          map.addSource('mapbox-dem', {
            type: 'raster-dem',
            url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
            tileSize: 512,
            maxzoom: 14,
          });
        }
        map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 });
      } else {
        map.setTerrain(null as any);
      }
    };
    if (map.isStyleLoaded()) {
      apply();
    } else {
      map.once('style.load', apply);
    }
  }, [showTerrain]);

  // Mapbox measures the container once; grid/layout changes require resize() or the canvas stays narrow.
  useEffect(() => {
    const el = mapContainerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const resizeMap = () => {
      const map = mapRef.current?.getMap();
      map?.resize();
    };
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(resizeMap);
    });
    ro.observe(el);
    resizeMap();
    return () => ro.disconnect();
  }, []);

  // --- Fetch GeoJSON for each layer ---
  const layersKey = useMemo(() => JSON.stringify(layers.map((l) => `${l.db}/${l.schema}/${l.table}/${l.geomColumn}/${l.sourceId ?? ''}`)), [layers]);

  useEffect(() => {
    if (!webMapCtx) return;
    let cancelled = false;

    // Publish the layer list up front so downstream widgets know which layers exist
    // and can render placeholders even before data lands.
    webMapCtx.setLayers(layers);

    layers.forEach((lyr) => {
      const geojsonPromise = lyr.sourceId != null
        ? fetchSourceGeoJson(lyr.sourceId, 2000)
        : fetchLayerGeoJson(lyr.schema, lyr.table, lyr.geomColumn, 2000, lyr.db);
      const columnsPromise = fetchLayerColumns(lyr.schema, lyr.table, lyr.db);

      // Publish DB-reported columns the moment they arrive so the field-binding
      // dropdowns unlock without waiting for (potentially slow) GeoJSON.
      columnsPromise
        .then((cols) => {
          if (cancelled || cols.length === 0) return;
          webMapCtx.setLayerSchemas((prev) => {
            const next = new Map(prev);
            next.set(lyr.id, cols);
            return next;
          });
        })
        .catch(() => { /* fall back to feature-inferred schema below */ });

      // Publish features as each layer's GeoJSON arrives; if /columns never gave us
      // a schema (source-catalog virtual layers, /columns failures), infer one from
      // the feature properties so the UI still unlocks.
      geojsonPromise
        .then((geojson) => {
          if (cancelled) return;
          const features = geojson.features ?? [];
          webMapCtx.setLayerFeatures((prev) => {
            const next = new Map(prev);
            next.set(lyr.id, features);
            return next;
          });
          webMapCtx.setLayerSchemas((prev) => {
            const existing = prev.get(lyr.id);
            if (existing && existing.length > 0) return prev;
            const inferred = inferColumnsFromFeatures(features);
            if (inferred.length === 0) return prev;
            const next = new Map(prev);
            next.set(lyr.id, inferred);
            return next;
          });
        })
        .catch(() => { /* leave features empty; dashboard still renders around the gap */ });
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layersKey]);

  // --- Auto-refresh timers per layer ---
  useEffect(() => {
    if (!webMapCtx) return;
    const refreshLayers = layers.filter((l) => l.autoRefresh && l.visible);
    if (refreshLayers.length === 0) return;

    const timers: ReturnType<typeof setInterval>[] = [];
    for (const lyr of refreshLayers) {
      const ms = Math.max((lyr.refreshIntervalMinutes ?? 1) * 60000, 6000);
      const timer = setInterval(async () => {
        try {
          const geojson = lyr.sourceId != null
            ? await fetchSourceGeoJson(lyr.sourceId, 2000)
            : await fetchLayerGeoJson(lyr.schema, lyr.table, lyr.geomColumn, 2000, lyr.db);
          const features = geojson.features ?? [];
          webMapCtx.setLayerFeatures((prev) => {
            const next = new Map(prev);
            next.set(lyr.id, features);
            return next;
          });

          // If the initial schema fetch failed (e.g. source-catalog layers), infer
          // the field list from the refreshed features so the binding UIs unlock.
          webMapCtx.setLayerSchemas((prev) => {
            const existing = prev.get(lyr.id);
            if (existing && existing.length > 0) return prev;
            const inferred = inferColumnsFromFeatures(features);
            if (inferred.length === 0) return prev;
            const next = new Map(prev);
            next.set(lyr.id, inferred);
            return next;
          });
        } catch { /* silently skip failed refresh */ }
      }, ms);
      timers.push(timer);
    }
    return () => timers.forEach(clearInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layersKey, webMapCtx]);

  // --- Register map actions for flyTo / flash ---
  useEffect(() => {
    if (!webMapCtx) return;
    webMapCtx.registerMapActions({
      flyTo: (lng, lat, zoom) => {
        mapRef.current?.getMap()?.flyTo({ center: [lng, lat], zoom: zoom ?? 15, duration: 1200 });
      },
      flash: (lng, lat) => {
        const map = mapRef.current?.getMap();
        if (!map) return;
        const id = `flash-${Date.now()}`;
        map.addSource(id, { type: 'geojson', data: { type: 'Point', coordinates: [lng, lat] } });
        map.addLayer({ id, type: 'circle', source: id, paint: { 'circle-radius': 20, 'circle-color': '#ffeb3b', 'circle-opacity': 0.8, 'circle-stroke-width': 2, 'circle-stroke-color': '#fff' } });
        let opacity = 0.8;
        const interval = setInterval(() => {
          opacity -= 0.1;
          if (opacity <= 0) {
            clearInterval(interval);
            try { map.removeLayer(id); map.removeSource(id); } catch { /* already removed */ }
            return;
          }
          try { map.setPaintProperty(id, 'circle-opacity', opacity); } catch { clearInterval(interval); }
        }, 80);
      },
    });
  }, [webMapCtx]);

  // --- Update extent on move ---
  const handleMoveEnd = useCallback(
    (e: ViewStateChangeEvent) => {
      setViewport(e.viewState);
      if (!webMapCtx || !mapRef.current) return;
      const map = mapRef.current.getMap();
      const bounds = map.getBounds();
      if (bounds) {
        const ext: MapExtent = {
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest(),
        };
        webMapCtx.setExtent(ext);
      }
    },
    [webMapCtx]
  );

  // --- Click handler for feature popups + measure ---
  const handleClick = useCallback(
    (e: MapLayerMouseEvent) => {
      if (activeTool === 'measure') {
        setMeasurePoints((prev) => [...prev, { lng: e.lngLat.lng, lat: e.lngLat.lat }]);
        return;
      }
      if (activeTool === 'draw') return;

      const features = e.features;
      if (!features?.length) {
        setPopupInfo(null);
        return;
      }
      const f = features[0];
      const props = (f.properties ?? {}) as Record<string, unknown>;
      const srcId = typeof f.source === 'string' ? f.source : '';
      const map = mapRef.current?.getMap();
      const clusterIdRaw = props.cluster_id;
      const pointCount = props.point_count;
      if (map && srcId && clusterIdRaw != null && pointCount != null) {
        const geoSrc = map.getSource(srcId) as GeoJSONSource | undefined;
        if (geoSrc && typeof geoSrc.getClusterExpansionZoom === 'function') {
          const clusterId = typeof clusterIdRaw === 'number' ? clusterIdRaw : Number(clusterIdRaw);
          geoSrc.getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err != null || zoom == null) return;
            const geom = f.geometry as GeoJSON.Point;
            const c = geom?.coordinates;
            if (c && c.length >= 2) {
              map.easeTo({ center: [c[0], c[1]], zoom });
            }
          });
          return;
        }
      }
      const matchLayer = layers.find((l) => `webmap-${l.id}` === srcId);
      setPopupInfo({
        lng: e.lngLat.lng,
        lat: e.lngLat.lat,
        properties: props,
        layerId: matchLayer?.id ?? srcId,
      });
      if (webMapCtx) {
        webMapCtx.setSelectedFeatures([f as unknown as GeoJSON.Feature]);
      }
    },
    [layers, webMapCtx, activeTool]
  );

  const interactiveLayerIds = useMemo(
    () =>
      layers.flatMap((l) => {
        const mode = l.displayMode ?? 'point';
        const prefix = `webmap-${l.id}`;
        if (mode === 'heatmap') return [];
        if (mode === 'cluster') return [`${prefix}-unclustered`, `${prefix}-cluster-circle`];
        const pointLayerId = categoryIconExpression(l) ? `${prefix}-symbol` : `${prefix}-circle`;
        return [`${prefix}-fill`, `${prefix}-line`, pointLayerId];
      }),
    [layers]
  );

  // --- GeoJSON data per layer from context (filtered by time + cross-filters, NOT by extent) ---
  type GeoJsonMap = Map<string, object>;
  const layerGeoJsonMap = useMemo((): GeoJsonMap => {
    const m: GeoJsonMap = new Map();
    if (!webMapCtx) return m;
    for (const lyr of layers) {
      const features = webMapCtx.getFilteredFeaturesForMap(lyr.id);
      m.set(lyr.id, { type: 'FeatureCollection', features });
    }
    return m;
  }, [layers, webMapCtx]);

  const widgetPopupEntries = useMemo(() => {
    if (!popupInfo) return [];
    const layer = layers.find((l) => l.id === popupInfo.layerId);
    const order = layer && webMapCtx?.layerSchemas.get(layer.id)?.map((c) => c.name);
    return popupPropertyEntries(
      popupInfo.properties,
      layer?.popupFields,
      order && order.length > 0 ? order : undefined,
    );
  }, [popupInfo, layers, webMapCtx]);

  // --- Bookmark handlers ---
  const handleAddBookmark = useCallback(
    (bm: MapBookmark) => {
      const next = [...bookmarks, bm];
      setBookmarks(next);
      if (onUpdateConfig) {
        onUpdateConfig({ ...widgetConfig, webMapOptions: { ...opts, bookmarks: next } });
      }
    },
    [bookmarks, onUpdateConfig, widgetConfig, opts],
  );

  const handleDeleteBookmark = useCallback(
    (id: string) => {
      const next = bookmarks.filter((b) => b.id !== id);
      setBookmarks(next);
      if (onUpdateConfig) {
        onUpdateConfig({ ...widgetConfig, webMapOptions: { ...opts, bookmarks: next } });
      }
    },
    [bookmarks, onUpdateConfig, widgetConfig, opts],
  );

  const handleGoToBookmark = useCallback(
    (bm: MapBookmark) => {
      const map = mapRef.current?.getMap();
      if (map) {
        map.flyTo({ center: [bm.center[0], bm.center[1]], zoom: bm.zoom, bearing: bm.bearing ?? 0, pitch: bm.pitch ?? 0 });
      }
    },
    [],
  );

  // --- Annotation handlers (draw tool) ---
  const handleAnnotationCreate = useCallback(
    (ann: MapAnnotation) => {
      if (!onUpdateConfig) return;
      const existing = opts.annotations ?? [];
      onUpdateConfig({ ...widgetConfig, webMapOptions: { ...opts, annotations: [...existing, ann] } });
    },
    [onUpdateConfig, widgetConfig, opts],
  );

  const handleAnnotationDelete = useCallback(
    (ids: string[]) => {
      if (!onUpdateConfig) return;
      const existing = opts.annotations ?? [];
      onUpdateConfig({ ...widgetConfig, webMapOptions: { ...opts, annotations: existing.filter((a) => !ids.includes(a.id)) } });
    },
    [onUpdateConfig, widgetConfig, opts],
  );

  // Clear measure when tool changes
  useEffect(() => {
    if (activeTool !== 'measure') setMeasurePoints([]);
  }, [activeTool]);

  // --- Geofence overlay GeoJSON from context ---
  const geofenceGeoJson = useMemo((): GeoJSON.FeatureCollection => {
    const fences = webMapCtx?.geofenceOverlay ?? [];
    return {
      type: 'FeatureCollection',
      features: fences.map((g) => ({
        type: 'Feature' as const,
        properties: {
          name: g.name,
          color: g.color ?? '#f44336',
          selected: g.id === webMapCtx?.geofenceSelectedId ? 1 : 0,
        },
        geometry: g.geometry,
      })),
    };
  }, [webMapCtx?.geofenceOverlay, webMapCtx?.geofenceSelectedId]);

  const handleGeofenceDrawDone = useCallback(
    (geometry: { type: 'Polygon'; coordinates: number[][][] }) => {
      webMapCtx?.onGeofenceDrawComplete.current?.(geometry);
      webMapCtx?.setGeofenceDrawMode(false);
    },
    [webMapCtx],
  );

  // --- Editor save callback ---
  const handleEditorSave = useCallback(
    (newOpts: WebMapWidgetOptions) => {
      if (!onUpdateConfig) return;
      onUpdateConfig({ ...widgetConfig, webMapOptions: newOpts });
      setEditorOpen(false);
    },
    [onUpdateConfig, widgetConfig, setEditorOpen]
  );

  return (
    <Box
      ref={mapContainerRef}
      sx={{
        width: '100%',
        minWidth: 0,
        height: '100%',
        minHeight: 280,
        flex: 1,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        '& .mapboxgl-ctrl button': {
          width: 24,
          height: 24,
        },
        '& .mapboxgl-ctrl button .mapboxgl-ctrl-icon': {
          width: 24,
          height: 24,
        },
        '& .mapboxgl-ctrl-group': {
          borderRadius: '4px',
        },
        '& .mapboxgl-ctrl-scale': {
          fontSize: 10,
          lineHeight: '16px',
          height: 16,
          px: 0.5,
        },
      }}
    >
      <ReactMapGL
        ref={mapRef}
        {...viewport}
        onMove={(e) => setViewport(e.viewState)}
        onMoveEnd={handleMoveEnd}
        onClick={handleClick}
        interactiveLayerIds={activeTool === 'measure' || activeTool === 'draw' ? [] : interactiveLayerIds}
        mapStyle={mapStyle}
        mapboxAccessToken={MAPBOX_TOKEN}
        projection={{name: 'mercator'}}
        style={{ width: '100%', height: '100%', minWidth: 0, minHeight: 0, flex: 1 }}
        cursor={activeTool === 'measure' || activeTool === 'draw' || webMapCtx?.geofenceDrawMode ? 'crosshair' : undefined}
        reuseMaps
      >
        {showNav && <NavigationControl position="top-right" />}
        {showGeo && <GeolocateControl position="top-right" />}
        {showFs && <FullscreenControl position="top-right" />}
        {showScale && <ScaleControl position="bottom-left" />}

        {ctrl.filterByExtent !== false && (
          <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 2 }}>
            <Tooltip title={webMapCtx?.filterByExtent ? 'Disable extent filter' : 'Filter widgets by visible extent'}>
              <IconButton
                size="small"
                onClick={() => webMapCtx?.setFilterByExtent(!webMapCtx.filterByExtent)}
                sx={{
                  bgcolor: webMapCtx?.filterByExtent ? 'primary.main' : 'background.paper',
                  color: webMapCtx?.filterByExtent ? 'primary.contrastText' : 'text.primary',
                  boxShadow: 2,
                  borderRadius: '4px',
                  '&:hover': { bgcolor: webMapCtx?.filterByExtent ? 'primary.dark' : 'action.hover' },
                }}
              >
                <CropFreeIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {layers.map((lyr, idx) => {
          if (!lyr.visible) return null;
          const srcId = `webmap-${lyr.id}`;
          const sourceKey = webMapGeoJsonSourceKey(srcId, lyr);
          const geojsonData = layerGeoJsonMap.get(lyr.id) ?? { type: 'FeatureCollection', features: [] };
          const fill = lyr.fillColor || LAYER_PALETTE[idx % LAYER_PALETTE.length];
          const stroke = lyr.strokeColor || fill;
          const mode = lyr.displayMode ?? 'point';
          const categorizedColor = categoryColorExpression(lyr, fill);
          const pointIcon = categoryIconExpression(lyr);

          if (mode === 'heatmap') {
            return (
              <Source key={sourceKey} id={srcId} type="geojson" data={geojsonData as GeoJSON.FeatureCollection}>
                <Layer
                  id={`${srcId}-heat`}
                  type="heatmap"
                  filter={['match', ['geometry-type'], ['Point', 'MultiPoint'], true, false]}
                  paint={{
                    'heatmap-radius': lyr.heatmapRadius ?? 20,
                    'heatmap-intensity': lyr.heatmapIntensity ?? 1,
                    'heatmap-opacity': lyr.opacity,
                    ...heatmapWeightPaint(lyr.heatmapWeight),
                    'heatmap-color': [
                      'interpolate', ['linear'], ['heatmap-density'],
                      0, 'rgba(0,0,255,0)',
                      0.2, 'royalblue',
                      0.4, 'cyan',
                      0.6, 'lime',
                      0.8, 'yellow',
                      1, 'red',
                    ],
                  } as HeatmapPaint}
                />
              </Source>
            );
          }

          if (mode === 'cluster') {
            return (
              <Source
                key={sourceKey}
                id={srcId}
                type="geojson"
                data={geojsonData as GeoJSON.FeatureCollection}
                cluster
                clusterRadius={lyr.clusterRadius ?? 50}
                clusterMaxZoom={lyr.clusterMaxZoom ?? 14}
              >
                <Layer
                  id={`${srcId}-cluster-circle`}
                  type="circle"
                  filter={['has', 'point_count']}
                  paint={{
                    'circle-color': fill,
                    'circle-radius': ['step', ['get', 'point_count'], 18, 50, 24, 200, 32],
                    'circle-opacity': lyr.opacity,
                    'circle-stroke-width': lyr.strokeWidth ?? 2,
                    'circle-stroke-color': '#fff',
                  }}
                />
                <Layer
                  id={`${srcId}-cluster-count`}
                  type="symbol"
                  filter={['has', 'point_count']}
                  layout={{
                    'text-field': ['get', 'point_count_abbreviated'] as unknown as string,
                    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                    'text-size': 12,
                    'text-allow-overlap': true,
                  }}
                  paint={{ 'text-color': '#fff' }}
                />
                <Layer
                  id={`${srcId}-unclustered`}
                  type="circle"
                  filter={['!', ['has', 'point_count']]}
                  paint={{
                    'circle-radius': lyr.pointRadius ?? 5,
                    'circle-color': stroke,
                    'circle-stroke-width': 1,
                    'circle-stroke-color': '#fff',
                    'circle-opacity': lyr.opacity,
                  }}
                />
              </Source>
            );
          }

          return (
            <Source key={sourceKey} id={srcId} type="geojson" data={geojsonData as GeoJSON.FeatureCollection}>
              <Layer
                id={`${srcId}-fill`}
                type="fill"
                filter={['match', ['geometry-type'], ['Polygon', 'MultiPolygon'], true, false]}
                paint={{ 'fill-color': categorizedColor, 'fill-opacity': lyr.opacity * 0.3 } as any}
              />
              <Layer
                id={`${srcId}-line`}
                type="line"
                filter={['match', ['geometry-type'], ['Polygon', 'MultiPolygon', 'LineString', 'MultiLineString'], true, false]}
                paint={{ 'line-color': lyr.categoricalStyle ? categorizedColor : stroke, 'line-width': lyr.strokeWidth ?? 2, 'line-opacity': lyr.opacity } as any}
              />
              {pointIcon ? (
                <Layer
                  key={`${srcId}-symbol`}
                  id={`${srcId}-symbol`}
                  type="symbol"
                  filter={['match', ['geometry-type'], ['Point', 'MultiPoint'], true, false]}
                  layout={{
                    'icon-image': pointIcon as unknown as string,
                    'icon-size': lyr.iconSize ?? 1,
                    'icon-allow-overlap': true,
                    'icon-ignore-placement': true,
                  }}
                  paint={{ 'icon-opacity': lyr.opacity, 'icon-color': categorizedColor } as any}
                />
              ) : (
                <Layer
                  key={`${srcId}-circle`}
                  id={`${srcId}-circle`}
                  type="circle"
                  filter={['match', ['geometry-type'], ['Point', 'MultiPoint'], true, false]}
                  paint={{
                    'circle-radius': lyr.pointRadius ?? 5,
                    'circle-color': categorizedColor,
                    'circle-stroke-width': lyr.strokeWidth ?? 1,
                    'circle-stroke-color': stroke,
                    'circle-opacity': lyr.opacity,
                  } as any}
                />
              )}
            </Source>
          );
        })}

        {/* Measure overlay */}
        {activeTool === 'measure' && (
          <MapMeasureOverlay
            points={measurePoints}
            onUndo={() => setMeasurePoints((p) => p.slice(0, -1))}
            onClear={() => setMeasurePoints([])}
          />
        )}

        {/* Draw control */}
        {activeTool === 'draw' && (
          <MapDrawControl
            onAnnotationCreate={handleAnnotationCreate}
            onAnnotationDelete={handleAnnotationDelete}
          />
        )}

        {/* Geofence overlay from context */}
        {geofenceGeoJson.features.length > 0 && (
          <Source id="__geofence_overlay" type="geojson" data={geofenceGeoJson}>
            <Layer
              id="__geofence_overlay_fill"
              type="fill"
              paint={{
                'fill-color': ['get', 'color'],
                'fill-opacity': ['case', ['==', ['get', 'selected'], 1], 0.3, 0.12],
              }}
            />
            <Layer
              id="__geofence_overlay_line"
              type="line"
              paint={{
                'line-color': ['get', 'color'],
                'line-width': ['case', ['==', ['get', 'selected'], 1], 3, 2],
              }}
            />
            <Layer
              id="__geofence_overlay_label"
              type="symbol"
              layout={{ 'text-field': ['get', 'name'], 'text-size': 11, 'text-allow-overlap': false }}
              paint={{ 'text-color': '#fff', 'text-halo-color': '#000', 'text-halo-width': 1 }}
            />
          </Source>
        )}

        {/* Geofence draw mode (triggered by geofence widget) */}
        {webMapCtx?.geofenceDrawMode && (
          <GeofenceDrawOverlay onComplete={handleGeofenceDrawDone} />
        )}

        {popupInfo && (
          <Popup
            longitude={popupInfo.lng}
            latitude={popupInfo.lat}
            onClose={() => setPopupInfo(null)}
            closeOnClick={false}
            maxWidth="320px"
          >
            <Stack spacing={0.5} sx={{ maxHeight: 200, overflow: 'auto', p: 0.5 }}>
              {widgetPopupEntries.map(([k, v]) => (
                <Typography key={k} variant="caption" component="div">
                  <strong>{k}:</strong> {String(v ?? '')}
                </Typography>
              ))}
            </Stack>
          </Popup>
        )}
      </ReactMapGL>

      {/* Geofence draw mode banner */}
      {webMapCtx?.geofenceDrawMode && (
        <Box sx={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 10, bgcolor: 'rgba(244,67,54,0.9)', borderRadius: 1, px: 2, py: 0.75, backdropFilter: 'blur(4px)' }}>
          <Typography variant="caption" color="white" fontWeight={600}>
            Drawing geofence — click to place vertices, double-click to finish
          </Typography>
        </Box>
      )}

      {/* Map Toolbar */}
      {layers.length > 0 && (
        <MapToolbar
          activeTool={activeTool}
          onToolChange={setActiveTool}
          showLegend={showLegend}
          onToggleLegend={() => setShowLegend((v) => !v)}
          showBookmarks={showBookmarks}
          onToggleBookmarks={() => setShowBookmarks((v) => !v)}
          hideDraw={!editMode}
        />
      )}

      {/* Legend overlay */}
      {showLegend && <MapLegendOverlay layers={layers} onClose={() => setShowLegend(false)} />}

      {/* Bookmarks panel */}
      {showBookmarks && (
        <MapBookmarksPanel
          bookmarks={bookmarks}
          onGoTo={handleGoToBookmark}
          onAdd={handleAddBookmark}
          onDelete={handleDeleteBookmark}
          onClose={() => setShowBookmarks(false)}
          currentCenter={[viewport.longitude, viewport.latitude]}
          currentZoom={viewport.zoom}
        />
      )}

      {/* Empty state */}
      {layers.length === 0 && (
        <Stack
          spacing={1.5}
          alignItems="center"
          justifyContent="center"
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(0,0,0,0.35)',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        >
          <MapIcon sx={{ fontSize: 40, color: 'common.white', opacity: 0.8 }} />
          <Typography variant="body2" color="common.white">
            No layers configured
          </Typography>
          {editMode && (
            <Button
              variant="contained"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => setEditorOpen(true)}
              sx={{ pointerEvents: 'auto' }}
            >
              Open Map Editor
            </Button>
          )}
        </Stack>
      )}

      {/* Full-screen editor dialog */}
      <WebMapEditorDialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSave={handleEditorSave}
        initialOptions={opts}
      />
    </Box>
  );
};

export default WebMapWidget;
